// web/src/routes/supplements/OrderingTab.jsx
// One product's owners, and what to do about each of them.
//
// Taylor's words: "Mark each owner's order status here, then place one
// combined order with the vendor." So this is not an order form — it is a
// column of decisions, and the order is what she reads off the bottom of it
// and sends to the vet's office herself. Nothing here places or pays for
// anything, which is her guardrail and the right one.
//
// TWO THINGS THAT LOOK THE SAME AND ARE NOT
//
//   flagged    the arithmetic says this shelf runs out inside the lead time.
//              Nobody has decided anything.
//   needed     a person put it on the list.
//
// A row can be flagged and not needed, which is the normal state of a thing
// that has just come due. Merging them would mean the app had quietly decided
// to buy something, and the first Taylor knew of it would be the invoice.
//
// RECEIVED IS NOT A LABEL
//
// It is the one option here that changes stock, and it does so exactly once —
// the server guards the transition rather than the state. The confirmation
// says the number out loud afterwards, because a shelf that silently grew by
// three is a shelf nobody can check.

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtDateNear } from '../../lib/dates';
import { Panel, Empty } from '../../components/dashboard/Planner';
import Stepper from '../../components/ui/Stepper';
import { friendly } from './SupplementDetail';

const OPTIONS = [
  { value: 'none', label: 'No order needed' },
  { value: 'needed', label: 'Need to order' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'received', label: 'Received' },
];

export default function OrderingTab({ supplementId, owners, today, canManage, onDone }) {
  const qc = useQueryClient();
  const [error, setError] = useState(null);
  const [said, setSaid] = useState(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['supplement', supplementId] });
    qc.invalidateQueries({ queryKey: ['supplements'] });
    qc.invalidateQueries({ queryKey: ['supplement-orders'] });
    qc.invalidateQueries({ queryKey: ['supplement-history'] });
    onDone?.();
  };

  const setStatus = useMutation({
    mutationFn: ({ ownerUserId, body }) =>
      api.put(`/supplements/${supplementId}/orders/${ownerUserId}`, body),
    onMutate: () => { setError(null); setSaid(null); },
    onSuccess: (r, { ownerUserId, body }) => {
      const who = owners.find((o) => o.ownerUserId === ownerUserId);
      if (body.status === 'received') {
        setSaid(`${who?.name}: ${r.received} added. ${r.containers} on the shelf.`);
      }
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  const setAutoship = useMutation({
    mutationFn: ({ ownerUserId, body }) =>
      api.put(`/supplements/${supplementId}/autoship/${ownerUserId}`, body),
    onMutate: () => { setError(null); setSaid(null); },
    onSuccess: refresh,
    onError: (e) => setError(friendly(e)),
  });

  const busy = setStatus.isPending || setAutoship.isPending;

  // What goes on the order. Only what somebody has actually committed to, so
  // the number is a thing she can read down a phone to the vet's office.
  const toOrder = owners.filter((o) => o.order?.status === 'needed');
  const total = toOrder.reduce((n, o) => n + (o.order?.quantity ?? 1), 0);

  if (owners.length === 0) {
    return (
      <Panel title="Ordering">
        <Empty>Nobody has stock or horses on this yet.</Empty>
      </Panel>
    );
  }

  return (
    <div className="space-y-4">
      <Panel title="Ordering">
        <p className="px-3 pt-2 text-xs text-ink-500">
          Mark each owner, then place one order with{' '}
          {owners[0]?.vendorName ?? 'the vendor'} yourself. Nothing here orders anything.
        </p>
        <ul className="planner-rows mt-2">
          {owners.map((o) => (
            <li key={o.ownerUserId} className="px-3 py-2">
              <OwnerOrder
                owner={o}
                today={today}
                canManage={canManage}
                busy={busy}
                onStatus={(body) => setStatus.mutate({ ownerUserId: o.ownerUserId, body })}
                onAutoship={(body) => setAutoship.mutate({ ownerUserId: o.ownerUserId, body })}
              />
            </li>
          ))}
        </ul>

        {said && <p className="px-3 py-2 text-sm text-hunter">{said}</p>}
        {error && <p className="px-3 py-2 text-sm text-rose-700">{error}</p>}
      </Panel>

      {/* The thing she actually sends. Deliberately a plain readable block
          rather than a button: the order is placed on a phone call or a
          website, and what this page owes her is the list to read out. */}
      <Panel title="This order" count={total || null}>
        {toOrder.length === 0 ? (
          <Empty>Nothing is marked to order.</Empty>
        ) : (
          <div className="px-3 py-2 text-sm">
            <ul className="space-y-0.5">
              {toOrder.map((o) => (
                <li key={o.ownerUserId} className="flex justify-between gap-3">
                  <span className="truncate">{o.name}</span>
                  <span className="tabular-nums shrink-0">{o.order.quantity}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 pt-2 border-t border-hunter/20 flex justify-between font-medium">
              <span>Total</span>
              <span className="tabular-nums">{total}</span>
            </p>
          </div>
        )}
      </Panel>
    </div>
  );
}

function OwnerOrder({ owner: o, today, canManage, busy, onStatus, onAutoship }) {
  const status = o.order?.status ?? 'none';
  const live = status === 'needed' || status === 'ordered';
  // The field starts at what is on the line, or at the suggestion, or at one.
  // A suggestion of ZERO is not a starting value — it means this shelf needs
  // nothing, and putting 0 in a field whose minimum is 1 shows a number the
  // stepper will not go back to. Somebody ordering for them anyway starts at
  // one, which is the smallest thing you can buy.
  const opening = (x) => String(x?.order?.quantity ?? (x.suggestedQuantity || 1));
  const [qty, setQty] = useState(opening(o));

  // Follow the server when the line changes underneath — marking a row
  // "needed" writes a quantity, and the field has to show what was written
  // rather than what was in it a moment before.
  const onLine = o.order?.quantity ?? null;
  const [followed, setFollowed] = useState(onLine);
  if (onLine !== followed) {
    setFollowed(onLine);
    setQty(opening(o));
  }

  // A stepper has no blur to save on, and saving on every tap would send
  // three requests for three taps. So it settles first: the last tap wins,
  // half a second later, as one write.
  useEffect(() => {
    if (!live) return undefined;
    const n = Number(qty);
    if (!Number.isInteger(n) || n <= 0 || n === o.order?.quantity) return undefined;
    const t = setTimeout(() => onStatus({ status, quantity: n }), 500);
    return () => clearTimeout(t);
    // onStatus is rebuilt every render by the parent; including it here would
    // restart the timer on each one and the write would never land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty, live, status, o.order?.quantity]);

  const flagged = o.status === 'due' || o.status === 'out';

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 flex-1">
          <span className="block font-medium truncate">{o.name}</span>
          <Why owner={o} today={today} flagged={flagged} status={status} />
        </span>
        <span className="shrink-0 text-right">
          <span className="block tabular-nums font-semibold leading-none">{o.containers}</span>
          <span className="block text-[11px] text-ink-400">on hand</span>
        </span>
      </div>

      {canManage && (
        <div className="mt-1.5 space-y-2">
          <select
            className="input w-full text-sm py-1.5"
            value={status}
            disabled={busy}
            onChange={(e) => {
              const next = e.target.value;
              const n = Number(qty);
              onStatus({
                status: next,
                quantity: next === 'none' || !Number.isInteger(n) || n <= 0 ? undefined : n,
              });
            }}
          >
            {OPTIONS.map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
                // Received is the one option that moves stock, so it is only
                // offered where there is something to receive. Letting it be
                // picked on an empty row would add containers nobody ordered.
                disabled={opt.value === 'received' && !live}
              >
                {opt.label}
              </option>
            ))}
          </select>

          {/* On its own line rather than beside the select: three 48px
              targets and a dropdown do not share 375 pixels, and the dropdown
              is the one that would have been squeezed. */}
          {(live || status === 'none') && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-ink-400 w-8 shrink-0">
                {live ? 'order' : 'qty'}
              </span>
              <Stepper
                value={qty}
                onChange={setQty}
                min={1}
                disabled={busy}
                label={`containers for ${o.name}`}
              />
              {/* Where a person has not committed yet, say what the number is
                  — otherwise a suggestion and a decision look identical. */}
              {!live && o.suggestedQuantity > 0 && Number(qty) === o.suggestedQuantity && (
                <span className="text-[11px] text-ink-400">suggested</span>
              )}
            </div>
          )}
        </div>
      )}

      {canManage && (
        <label className="mt-1.5 flex items-center gap-1.5 text-xs text-ink-500">
          <input
            type="checkbox"
            checked={!!o.autoship}
            disabled={busy}
            onChange={(e) => onAutoship(e.target.checked ? { active: true, quantity: 1 } : { active: false })}
          />
          <span>
            On a client-managed autoship
            {o.autoship?.nextExpected && (
              <span className="text-ink-400"> · next {fmtDateNear(o.autoship.nextExpected, today)}</span>
            )}
          </span>
        </label>
      )}
    </div>
  );
}

/**
 * Why this row is here — and it is never both reasons at once.
 *
 * A status somebody chose outranks the arithmetic, because once a person has
 * said "ordered" the interesting fact is that it is coming, not that it was
 * once low.
 */
function Why({ owner: o, today, flagged, status }) {
  if (status === 'ordered') {
    return (
      <span className="block text-xs text-hunter">
        Ordered{o.order?.placedOn ? ` ${fmtDateNear(o.order.placedOn, today)}` : ''} · {o.order?.quantity} coming
      </span>
    );
  }
  if (status === 'needed') {
    return <span className="block text-xs text-amber-700 font-medium">On the list · {o.order?.quantity}</span>;
  }
  if (o.status === 'autoship') {
    return (
      <span className="block text-xs text-sky-700">
        Arrives on autoship{o.autoship?.nextExpected ? ` ${fmtDateNear(o.autoship.nextExpected, today)}` : ''}
      </span>
    );
  }
  if (flagged) {
    return (
      <span className="block text-xs text-amber-700">
        {o.containers <= 0 ? 'Out' : 'Running low'}
        {o.emptyOn ? ` · empty ${fmtDateNear(o.emptyOn, today)}` : ''}
        {o.suggestedQuantity ? ` · suggest ${o.suggestedQuantity}` : ''}
      </span>
    );
  }
  if (o.status === 'unknown') {
    return <span className="block text-xs text-ink-400">No date yet — needs a container size or a dose</span>;
  }
  if (o.status === 'idle') {
    return <span className="block text-xs text-ink-400">No horses on it</span>;
  }
  if (o.reorderOn) {
    return <span className="block text-xs text-ink-400">Fine until {fmtDateNear(o.reorderOn, today)}</span>;
  }
  return null;
}
